import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import config from '../config/config';

window.Pusher = Pusher;

let echoInstance = null;

/**
 * Initialize Laravel Echo instance
 */
export const initializeEcho = () => {
  if (echoInstance) {
    return echoInstance;
  }

  const token = localStorage.getItem('token');

  // Hosted Pusher Channels: pusher-js connects to wss://ws-<cluster>.pusher.com,
  // so no wsHost/wsPort is set — the cluster determines the endpoint. This needs
  // no self-hosted WebSocket server, proxy, or open port.
  echoInstance = new Echo({
    broadcaster: 'pusher',
    key: process.env.REACT_APP_PUSHER_KEY || '',
    cluster: process.env.REACT_APP_PUSHER_CLUSTER || 'mt1',
    forceTLS: true,
    disableStats: true,
    authEndpoint: `${config.apiBaseUrl}/broadcasting/auth`,
    auth: {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    },
  });

  return echoInstance;
};

/**
 * Get Echo instance (initializes if not already)
 */
export const getEcho = () => {
  if (!echoInstance) {
    return initializeEcho();
  }
  return echoInstance;
};

/**
 * Update auth token for Echo
 */
export const updateEchoAuth = () => {
  const token = localStorage.getItem('token');
  if (echoInstance) {
    echoInstance.connector.options.auth.headers.Authorization = `Bearer ${token}`;
  }
};

/**
 * Join a multiplayer room channel
 */
export const joinRoom = (roomCode, callbacks = {}) => {
  const echo = getEcho();
  const channelName = `multiplayer.${roomCode}`;

  // Leave any existing subscription first so listeners can't stack up
  // (StrictMode / re-renders can otherwise register the same listener twice).
  // Only leave if the connection is already established — calling leave() during
  // the WebSocket handshake triggers "closed before connection established".
  if (echo.connector?.pusher?.connection?.state === 'connected') {
    echo.leave(channelName);
  }

  const channel = echo.join(channelName);

  // Presence channel events
  if (callbacks.onHere) {
    channel.here(callbacks.onHere);
  }

  if (callbacks.onJoining) {
    channel.joining(callbacks.onJoining);
  }

  if (callbacks.onLeaving) {
    channel.leaving(callbacks.onLeaving);
  }

  // Custom events
  if (callbacks.onPlayerJoined) {
    channel.listen('.player.joined', callbacks.onPlayerJoined);
  }

  if (callbacks.onPlayerLeft) {
    channel.listen('.player.left', callbacks.onPlayerLeft);
  }

  if (callbacks.onPlayerKicked) {
    channel.listen('.player.kicked', callbacks.onPlayerKicked);
  }

  if (callbacks.onGameStarted) {
    channel.listen('.game.started', callbacks.onGameStarted);
  }

  if (callbacks.onRoundStarted) {
    channel.listen('.round.started', callbacks.onRoundStarted);
  }

  if (callbacks.onPlayerGuessed) {
    channel.listen('.player.guessed', callbacks.onPlayerGuessed);
  }

  if (callbacks.onRoundCompleted) {
    channel.listen('.round.completed', callbacks.onRoundCompleted);
  }

  if (callbacks.onGameCompleted) {
    channel.listen('.game.completed', callbacks.onGameCompleted);
  }

  return channel;
};

/**
 * Leave a multiplayer room channel
 */
export const leaveRoom = (roomCode) => {
  const echo = getEcho();
  echo.leave(`multiplayer.${roomCode}`);
};

/**
 * Disconnect from Echo
 */
export const disconnect = () => {
  if (echoInstance) {
    echoInstance.disconnect();
    echoInstance = null;
  }
};

const websocketService = {
  initializeEcho,
  getEcho,
  updateEchoAuth,
  joinRoom,
  leaveRoom,
  disconnect,
};

export default websocketService;
